import { useState } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { getAccessToken } from '../utils/tokenUtils';

/**
 * Custom hook for Stripe subscription and portal functionality.
 * Connects to backend endpoints:
 * - POST /stripe/create-checkout-session
 * - POST /stripe/create-portal-session
 */
const useStripeSubscription = () => {
  const [loading, setLoading] = useState(false);

  /**
   * Initiates a Stripe checkout session for subscription
   * @param {string} priceId - Stripe Price ID for the subscription
   * @param {string} plan - Plan tier (e.g., 'pro')
   * @param {string} billingCycle - Optional billing cycle ('monthly', 'yearly')
   */
  const handleSubscribe = async (priceId, plan = 'pro', billingCycle = 'monthly') => {
    setLoading(true);
    try {
      const successUrl = `${window.location.origin}/vendor/success/{CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/vendor/plans`;

      const token = getAccessToken();
      // alert(`priceId: ${priceId}, plan: ${plan}, billingCycle: ${billingCycle}, successUrl: ${successUrl}, cancelUrl: ${cancelUrl}`);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/vendor/stripe/create-checkout-session`,
        {
          price_id: priceId,
          success_url: successUrl,
          cancel_url: cancelUrl,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const { url, checkout_session_id: sessionId } = response.data;

      // Redirect vendor to Stripe Checkout
      if (url) {
        window.location.href = url;
        return;
      }

      // Fallback: if only sessionId is returned
      if (sessionId) {
        window.location.href = successUrl.replace('{CHECKOUT_SESSION_ID}', sessionId);
        return;
      }

      throw new Error('Checkout URL could not be resolved.');
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to process subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Opens Stripe Customer Portal for the vendor
   */
  const openCustomerPortal = async () => {
    setLoading(true);
    try {
      const token = getAccessToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/vendor/stripe/create-portal-session`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const { portal_url: url } = response.data;
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('Portal URL could not be resolved.');
      }
    } catch (error) {
      console.error('Portal session error:', error);
      toast.error('Failed to open customer portal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cancels the Stripe subscription
   * @param {string} subscriptionId - The Stripe subscription ID to cancel
   */
  const handleCancelSubscription = async (subscriptionId) => {
    if (!subscriptionId) {
      toast.error('Subscription ID is missing');
      return;
    }

    setLoading(true);
    try {
      const token = getAccessToken();
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/vendor/stripe/subscription`,
        {
          subscription_id: subscriptionId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        toast.success('Subscription cancelled successfully');
        // Reload page to update UI state
        window.location.reload();
      }
    } catch (error) {
      console.error('Cancel subscription error:', error);
      toast.error(error.response?.data?.error || 'Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return {
    handleSubscribe,
    openCustomerPortal,
    handleCancelSubscription,
    loading,
  };
};

export default useStripeSubscription;
